import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  FaCheckCircle,
  FaEdit,
  FaExclamationTriangle,
  FaPlus,
  FaSearch,
  FaTrash,
  FaUserSlash,
  FaUserTie,
} from "react-icons/fa";
import Header from "../components/AdminHeader"

export default function StaffDatabase() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [form, setForm] = useState({
    staff_name: "",
    staff_roblox_username: "",
    email: "",
    role: "",
    infraction_1: false,
    infraction_2: false,
    infraction_3: false,
  });

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    setPageError("");
    setToast("");

    const { data, error } = await supabase
      .from("staff")
      .select(
        "id, staff_name, staff_roblox_username, email, role, infraction_1, infraction_2, infraction_3, suspended, created_at"
      )
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch staff:", error);
      setRows([]);
      setPageError("Could not load staff records.");
      setLoading(false);
      return;
    }

    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      return (
        (row.staff_name || "").toLowerCase().includes(q) ||
        (row.staff_roblox_username || "").toLowerCase().includes(q) ||
        (row.email || "").toLowerCase().includes(q) ||
        (row.role || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search]);

  const openCreateModal = () => {
    setEditingStaff(null);
    setForm({
      staff_name: "",
      staff_roblox_username: "",
      email: "",
      role: "",
      infraction_1: false,
      infraction_2: false,
      infraction_3: false,
    });
    setModalOpen(true);
  };

  const openEditModal = (staff) => {
    setEditingStaff(staff);
    setForm({
      staff_name: staff.staff_name || "",
      staff_roblox_username: staff.staff_roblox_username || "",
      email: staff.email || "",
      role: staff.role || "",
      infraction_1: !!staff.infraction_1,
      infraction_2: !!staff.infraction_2,
      infraction_3: !!staff.infraction_3,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (formLoading) return;
    setModalOpen(false);
    setEditingStaff(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveStaff = async (e) => {
    e.preventDefault();
    setPageError("");
    setToast("");

    if (
      !form.staff_name.trim() ||
      !form.staff_roblox_username.trim() ||
      !form.email.trim() ||
      !form.role.trim()
    ) {
      setPageError("Please complete all staff fields.");
      return;
    }

    setFormLoading(true);

    const payload = {
      staff_name: form.staff_name.trim(),
      staff_roblox_username: form.staff_roblox_username.trim(),
      email: form.email.trim().toLowerCase(),
      role: form.role.trim(),
      infraction_1: !!form.infraction_1,
      infraction_2: !!form.infraction_2,
      infraction_3: !!form.infraction_3,
    };

    if (editingStaff) {
      const { error } = await supabase
        .from("staff")
        .update(payload)
        .eq("id", editingStaff.id);

      if (error) {
        console.error("Failed to update staff:", error);
        setPageError("Could not update staff record.");
        setFormLoading(false);
        return;
      }

      setToast("Staff record updated.");
    } else {
      const { error } = await supabase.from("staff").insert({
        ...payload,
        suspended: false,
      });

      if (error) {
        console.error("Failed to create staff:", error);
        setPageError("Could not create staff record.");
        setFormLoading(false);
        return;
      }

      setToast("Staff member added.");
    }

    setFormLoading(false);
    closeModal();
    await fetchStaff();
  };

  const handleDeleteStaff = async (staff) => {
    const confirmed = window.confirm(
      `Delete ${staff.staff_name} from the staff database?`
    );

    if (!confirmed) return;

    const { error } = await supabase.from("staff").delete().eq("id", staff.id);

    if (error) {
      console.error("Failed to delete staff:", error);
      setPageError("Could not delete staff record.");
      return;
    }

    setToast("Staff record deleted.");
    await fetchStaff();
  };

  const handleSuspendStaff = async (staff) => {
    const { error } = await supabase
      .from("staff")
      .update({ suspended: !staff.suspended })
      .eq("id", staff.id);

    if (error) {
      console.error("Failed to suspend staff:", error);
      setPageError("Could not update suspension status.");
      return;
    }

    setToast(
      `${staff.staff_name} has been ${staff.suspended ? "unsuspended" : "suspended"}.`
    );
    await fetchStaff();
  };

  const handleToggleInfraction = async (staff, fieldName) => {
    const { error } = await supabase
      .from("staff")
      .update({
        [fieldName]: !staff[fieldName],
      })
      .eq("id", staff.id);

    if (error) {
      console.error("Failed to update infraction:", error);
      setPageError("Could not update infractions.");
      return;
    }

    await fetchStaff();
  };

  return (
    <>
    <Header />
    <div className="min-h-screen bg-[#08111f] text-white">
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative mx-auto max-w-[1800px] px-4 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
        <section className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(135deg,rgba(86,24,113,0.45),rgba(39,78,125,0.45),rgba(18,111,135,0.40))] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl">
          <div className="grid gap-8 px-5 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.1fr_0.9fr] lg:px-10 lg:py-10">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
                <FaUserTie />
                <span>Staff Database</span>
              </div>

              <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl">
                Benton Staff Records
              </h1>

              <p className="mt-4 max-w-3xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
                Manage all staff members, roles, infractions, and suspension status.
              </p>
            </div>

            <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur-xl sm:p-5">
              <div className="mb-4 text-sm font-medium text-white/90">
                Staff summary
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                    Total staff
                  </div>
                  <div className="mt-2 text-xl font-semibold text-white">
                    {rows.length}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                  <div className="text-xs uppercase tracking-[0.24em] text-white/55">
                    Visible records
                  </div>
                  <div className="mt-2 text-xl font-semibold text-white">
                    {filteredRows.length}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-[420px]">
              <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, Roblox username, email or role"
                className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
              />
            </div>

            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex h-12 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110"
            >
              <FaPlus />
              <span>Add Staff</span>
            </button>
          </div>

          {pageError ? (
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
              <FaExclamationTriangle className="mt-0.5 shrink-0" />
              <span>{pageError}</span>
            </div>
          ) : null}

          {toast ? (
            <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
              <FaCheckCircle className="mt-0.5 shrink-0" />
              <span>{toast}</span>
            </div>
          ) : null}

          {loading ? (
            <div className="grid min-h-[240px] place-items-center rounded-[24px] border border-white/10 bg-white/[0.04] text-sm text-white/70">
              Loading staff records...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[1450px]">
                <div className="mb-3 grid grid-cols-[1.5fr_1.5fr_1.8fr_1fr_1.1fr_1.5fr] gap-4 px-4 text-xs uppercase tracking-[0.24em] text-white/45">
                  <div>Staff Name</div>
                  <div>Staff Roblox Username</div>
                  <div>Email</div>
                  <div>Role</div>
                  <div>Infractions</div>
                  <div>Actions</div>
                </div>

                <div className="space-y-3">
                  {filteredRows.length === 0 ? (
                    <div className="rounded-[24px] border border-white/10 bg-white/[0.04] px-5 py-8 text-center text-sm text-white/70">
                      No staff records found.
                    </div>
                  ) : (
                    filteredRows.map((staff) => (
                      <div
                        key={staff.id}
                        className="grid grid-cols-[1.5fr_1.5fr_1.8fr_1fr_1.1fr_1.5fr] items-center gap-4 rounded-[24px] border border-white/10 bg-white/[0.04] px-4 py-4"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-white">
                            {staff.staff_name}
                          </div>
                          <div className="mt-1 text-xs text-white/45">
                            {staff.suspended ? "Suspended" : "Active"}
                          </div>
                        </div>

                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-white">
                            {staff.staff_roblox_username}
                          </div>
                        </div>

                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-white">
                            {staff.email}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm font-medium text-white">
                            {staff.role || "—"}
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-2 text-xs text-white/75">
                              <input
                                type="checkbox"
                                checked={!!staff.infraction_1}
                                onChange={() =>
                                  handleToggleInfraction(staff, "infraction_1")
                                }
                                className="h-4 w-4 rounded border-white/20 bg-white/10"
                              />
                              <span>1</span>
                            </label>

                            <label className="flex items-center gap-2 text-xs text-white/75">
                              <input
                                type="checkbox"
                                checked={!!staff.infraction_2}
                                onChange={() =>
                                  handleToggleInfraction(staff, "infraction_2")
                                }
                                className="h-4 w-4 rounded border-white/20 bg-white/10"
                              />
                              <span>2</span>
                            </label>

                            <label className="flex items-center gap-2 text-xs text-white/75">
                              <input
                                type="checkbox"
                                checked={!!staff.infraction_3}
                                onChange={() =>
                                  handleToggleInfraction(staff, "infraction_3")
                                }
                                className="h-4 w-4 rounded border-white/20 bg-white/10"
                              />
                              <span>3</span>
                            </label>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(staff)}
                              className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-sm text-white transition hover:bg-white/[0.10]"
                            >
                              <FaEdit />
                              <span>Edit</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteStaff(staff)}
                              className="inline-flex items-center gap-2 rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-sm text-red-100 transition hover:bg-red-500/20"
                            >
                              <FaTrash />
                              <span>Delete</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleSuspendStaff(staff)}
                              className="inline-flex items-center gap-2 rounded-xl border border-amber-300/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-100 transition hover:bg-amber-500/20"
                            >
                              <FaUserSlash />
                              <span>{staff.suspended ? "Unsuspend" : "Suspend"}</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-[620px] rounded-[28px] border border-white/10 bg-[#101a2b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                Staff management
              </div>
              <h3 className="mt-1 text-2xl font-semibold text-white">
                {editingStaff ? "Edit Staff Member" : "Add Staff Member"}
              </h3>
            </div>

            <form onSubmit={handleSaveStaff} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-white/82">
                  Staff Name
                </label>
                <input
                  type="text"
                  name="staff_name"
                  value={form.staff_name}
                  onChange={handleInputChange}
                  placeholder="Enter staff name"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-white/82">
                  Staff Roblox Username
                </label>
                <input
                  type="text"
                  name="staff_roblox_username"
                  value={form.staff_roblox_username}
                  onChange={handleInputChange}
                  placeholder="Enter Roblox username"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-white/82">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="Enter email"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-white/82">
                  Role
                </label>
                <input
                  type="text"
                  name="role"
                  value={form.role}
                  onChange={handleInputChange}
                  placeholder="Enter staff role"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-white/82">
                  Infractions
                </label>

                <div className="flex items-center gap-6 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-4">
                  <label className="flex items-center gap-2 text-sm text-white/80">
                    <input
                      type="checkbox"
                      name="infraction_1"
                      checked={form.infraction_1}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-white/20 bg-white/10"
                    />
                    <span>Infraction 1</span>
                  </label>

                  <label className="flex items-center gap-2 text-sm text-white/80">
                    <input
                      type="checkbox"
                      name="infraction_2"
                      checked={form.infraction_2}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-white/20 bg-white/10"
                    />
                    <span>Infraction 2</span>
                  </label>

                  <label className="flex items-center gap-2 text-sm text-white/80">
                    <input
                      type="checkbox"
                      name="infraction_3"
                      checked={form.infraction_3}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-white/20 bg-white/10"
                    />
                    <span>Infraction 3</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-3 text-sm font-medium text-white transition hover:bg-white/[0.10]"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={formLoading}
                  className="rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-4 py-3 text-sm font-semibold text-white transition hover:brightness-110 disabled:opacity-60"
                >
                  {formLoading
                    ? editingStaff
                      ? "Saving..."
                      : "Adding..."
                    : editingStaff
                      ? "Save Changes"
                      : "Add Staff"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
    </>
  );
}